// types
import { OKVItemType, OKVProduct, type CategoryTreeRoot, type OKVCategory, type OKVItem, type OKVTile, type Props } from 'components/ContentElementsGdds/online-catalog-assets';
import type { Config } from '../types';

// utils
import { makeProxyRequest } from '../../proxy-fetch-service';

async function getProductCount(cat: OKVItem, brand: string | null, headers: Config<Props>['headers']): Promise<number> {
  const response = await makeProxyRequest<{ total: number }>(
    'GET',
    ['catalog', 'products'],
    new URLSearchParams({
      lang: cat.catalogLocale,
      ...(brand && { brand }),
      responseSize: 'list',
      limit: '1',
      'filter.query': `categories.id:subtree(%22${cat.identifier}%22)`,
    }),
    headers,
  );

  return response?.total || 0;
}

function getParentInCategoryTree(
  categoryTree: CategoryTreeRoot | OKVCategory | undefined,
  identifier: string,
  parentIdentifier?: string,
  parentName?: string
): string | null {
  if (categoryTree?.id === identifier && parentIdentifier && parentName) {
    return parentName;
  }
  if (categoryTree?.subCategories) {
    let parent;
    for (const category of categoryTree.subCategories) {
      parent = getParentInCategoryTree(category, identifier, categoryTree?.id, categoryTree?.name);
      if (parent) return parent;
    }
  }
  return null;
}

export async function getCategoryStore(
  categoryItems: OKVItem[],
  brandName: string | null,
  headers: Config<Props>['headers'],
  hideCount: boolean,
) {
  const resp = await Promise.all(
    categoryItems.map((cat) => (async () => {
      const response = await makeProxyRequest(
        'GET',
        ['categories', 'v1', cat.catalogLocale, 'categories'],
        new URLSearchParams({
          rootId: cat.identifier,
          depth: '0',
          contentFilter: 'products',
          brand: brandName ?? '',
        }),
        headers,
      );
      const okvCategory = response as OKVCategory;
      let total: number | undefined = undefined;

      if (!hideCount) {
        total = await getProductCount(cat, brandName, headers);
      }

      return {
        okvCategory: { ...okvCategory, total },
      };
    })(),)
  );

  return resp.reduce(
    (acc, { okvCategory }) => {
      if (okvCategory?.id) {
        acc[okvCategory.id] = okvCategory;
      }
      return acc;
    },
    {} as Record<string, OKVCategory>,
  );
}

export async function fetchCategoryTree(
  catalogLocales: string[],
  brand: string | null,
  headers: Config<Props>['headers'],
) {
  const categoryPromises = catalogLocales.map((locale) =>
    makeProxyRequest<CategoryTreeRoot>(
      'GET',
      ['categories', 'v1', locale, 'categories'],
      new URLSearchParams(brand ? { brand } : {}),
      headers,
    ),
  );

  try {
    return await Promise.all(categoryPromises);
  } catch {
    return null;
  }
}

export function itemToTile(
  item: OKVItem,
  categoriesByLanguage: Record<string, CategoryTreeRoot | undefined>,
  categoriesStore?: Record<string, OKVCategory>,
): OKVTile | OKVItem {
  const itemExistsInCategoryStore = Boolean(item?.identifier && categoriesStore?.[item.identifier]);

  if (itemExistsInCategoryStore) {
    let imageURL: string | undefined;
    const category = categoriesStore![item.identifier];
    const isSeries = category?.categoryType === OKVItemType.Series;

    // if we want to display a series tiles we have to use an ambience image (small, medium or large)
    if (category?.categoryType === OKVItemType.Series) {
      imageURL = category?.images?.[1]?.small ?? category?.images?.[0]?.thumbnail;
    } else {
      imageURL = category?.images?.[0]?.thumbnail;
    }

    return {
      ...item,
      product: {
        name: isSeries
          ? category?.name
          : getParentInCategoryTree(categoriesByLanguage[item.catalogLocale], item.identifier),
        pictureObject: {
          url: imageURL,
        } as PictureObject,
        subtitle: isSeries ? category?.slogan : category?.name,
      } as OKVProduct,
      total: category?.total ?? 0,
    } as OKVTile;
  } else if (categoriesStore) {
    // store is loaded and item doesn't exist in it
    return { ...item, product: { deprecated: true } } as OKVTile;
  }

  return item;
}
